import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";

export default function Testimonials() {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <SectionHeader
          index="05"
          label="Client Words"
          testid="testimonials-heading"
          title={
            <>
              Results worth <span className="italic text-[#EB5E28]">quoting</span>.
            </>
          }
        />

        <Reveal className="mt-14" delay={0.1}>
          <figure
            data-testid="testimonial-card-john-parker"
            className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-md sm:p-12"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-10 top-6 select-none font-display text-[7rem] leading-none text-[#EB5E28]/10 sm:text-[10rem]"
            >
              &rdquo;
            </span>

            <blockquote>
              <p className="relative font-display text-2xl font-medium leading-snug text-[#0F141A] sm:text-3xl">
                Thom was one of the key reasons our transformation succeeded and endured. Plenty of
                engineers can map waste. Thom is the rare professional who can take a complex
                operational redesign, implement it with precision, and{" "}
                <span className="italic text-[#EB5E28]">make the results stick</span>.
              </p>

              <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
                <div>
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#008B9B]">
                    Building the improvement engine
                  </p>
                  <p className="text-sm leading-relaxed text-[#4A5361] lg:text-base">
                    I had the privilege of leading the Lean Six Sigma program at Independence Blue
                    Cross, and Thom's work integrating our Yellow Belts was a turning point. He
                    didn't simply train them; he built a functioning improvement engine. He aligned
                    staff with new workflows, equipped them with practical tools, and ensured they
                    were confident driving change on the floor. That alignment is the reason our
                    early wins scaled instead of stalling.
                  </p>
                </div>
                <div>
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#008B9B]">
                    Sustaining performance
                  </p>
                  <p className="text-sm leading-relaxed text-[#4A5361] lg:text-base">
                    Thom also took responsibility for the most difficult part of any deployment:
                    sustaining performance. He led the Control phase with discipline, partnering
                    with our informatics team to build and launch comprehensive dashboards that gave
                    leadership real-time visibility into throughput, quality, and efficiency. Those
                    dashboards became the backbone of our governance model and are still in use
                    today.
                  </p>
                </div>
              </div>

              <p className="mt-10 border-l-2 border-[#EB5E28] pl-6 text-base leading-relaxed text-[#0F141A] sm:text-lg">
                Thom brings a combination of technical rigor, operational intuition, and
                people-centered leadership that is exceptionally hard to find. If your organization
                is serious about durable, metric-driven improvement, Thom is the person you want
                driving it.
              </p>
            </blockquote>

            <figcaption className="mt-10 flex flex-col gap-6 border-t border-[#F1F5F9] pt-8 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <a
                  href="https://www.xleratorai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="testimonial-logo-link"
                  aria-label="Visit XLeratorAI"
                  className="relative flex items-center rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 shadow-sm transition-shadow duration-300 hover:shadow-md"
                >
                  <img
                    src="/leratorai-logo.png"
                    alt="XLeratorAI logo"
                    className="h-8 w-auto"
                    loading="lazy"
                  />
                  <ArrowUpRight className="absolute -right-1.5 -top-1.5 h-4 w-4 rounded-full bg-[#0071D1] p-0.5 text-white" />
                </a>
                <div>
                  <p className="text-sm font-semibold text-[#0F141A]">John Parker</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626D7D]">
                    Founder and CEO · <span className="font-semibold text-[#0071D1]">XLeratorAI</span>
                  </p>
                </div>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8B95A5]">
                Re: Lean Six Sigma program · Independence Blue Cross
              </p>
            </figcaption>
          </figure>
        </Reveal>

        <div className="mx-auto mt-6 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal className="h-full" delay={0.15}>
            <figure
              data-testid="testimonial-card-michele-r"
              className="flex h-full flex-col rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-md sm:p-10"
            >
              <blockquote>
                <p className="font-display text-xl font-medium leading-snug text-[#0F141A]">
                  Thom has a talent for{" "}
                  <span className="italic text-[#EB5E28]">matching people to projects</span> —
                  identifying strengths and partnering them for the greatest success.
                </p>
                <p className="mt-5 text-sm leading-relaxed text-[#4A5361]">
                  This applies to both project delivery and personal career growth. As a program
                  manager, I benefited from the Business Analyst Center of Excellence, knowing that
                  regardless of who was partnered on a project, their process and outcomes would be
                  top-notch. In the current fast-paced AI-driven delivery enablement underway across
                  all industries, a critical component will continue to be the accuracy and
                  completeness of requirements. The human element remains at the forefront as
                  technology can only build as good as can be defined. People and process will
                  become more critical to the enablement of successful AI-driven initiatives. Thom's
                  expertise in these areas is to be appreciated.
                </p>
                <p className="mt-5 text-sm font-semibold leading-relaxed text-[#0F141A]">
                  I'd welcome the opportunity to work with him again.
                </p>
              </blockquote>
              <figcaption className="mt-auto flex items-center gap-4 border-t border-[#F1F5F9] pt-6 pt-8">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F1F5F9] font-display text-lg font-semibold text-[#008B9B]">
                  M
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#0F141A]">Michele R.</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626D7D]">
                    Healthcare Analytics Director
                  </p>
                </div>
              </figcaption>
            </figure>
          </Reveal>

          <Reveal className="h-full" delay={0.2}>
            <figure
              data-testid="testimonial-card-laith-e"
              className="flex h-full flex-col rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-md sm:p-10"
            >
              <blockquote>
                <p className="font-display text-xl font-medium leading-snug text-[#0F141A]">
                  Thom helped me build and{" "}
                  <span className="italic text-[#EB5E28]">strengthen</span> our Business Systems
                  Analysis practice at Independence Blue Cross, leading more than 50 associates.
                </p>
                <p className="mt-5 text-sm leading-relaxed text-[#4A5361]">
                  He combined intelligence, practical expertise, and strong character to develop a
                  high-performing organization. One example was his work establishing a tools and
                  analytics team that faced a persistent backlog, competing priorities, and
                  difficulty meeting important milestones. Thom organized the team around Scrum and
                  redesigned the workflow, bringing greater clarity to priorities and structure to
                  delivery. Under his leadership, the team shifted to consistently delivering
                  high-quality work and meeting key milestones.
                </p>
                <p className="mt-5 text-sm font-semibold leading-relaxed text-[#0F141A]">
                  What made Thom effective was his attention to both process and people. He built
                  trust, brought teams together around shared goals, and created the discipline
                  needed to sustain improvement. I would confidently recommend him to leaders who
                  need help strengthening an organization or getting an important initiative moving.
                </p>
              </blockquote>
              <figcaption className="mt-auto flex items-center gap-4 border-t border-[#F1F5F9] pt-8">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F1F5F9] font-display text-lg font-semibold text-[#008B9B]">
                  L
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#0F141A]">Laith E.</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626D7D]">
                    SVP Business Transformation
                  </p>
                </div>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
